import { useEffect, useMemo, useState } from "react";
import { Reveal } from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FALLBACK_BRANCHES } from "@/data/site";

const inputCls =
  "w-full bg-panel2 border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-gold focus:outline-none transition-colors";

const STATUS_OPTIONS = ["new", "contacted", "closed"];

const branchName = (slug) => FALLBACK_BRANCHES.find((b) => b.slug === slug)?.name || slug;

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
      toast.success("Welcome back, admin.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <Reveal>
        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="w-full max-w-md clip-frame border border-white/10 bg-panel p-8 md:p-10"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-3">Restricted</p>
          <h1 className="font-display uppercase text-3xl mb-8">Admin Login</h1>
          <div className="space-y-5">
            <input
              type="email"
              data-testid="admin-login-email-input"
              className={inputCls}
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              type="password"
              data-testid="admin-login-password-input"
              className={inputCls}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-submit-button"
            disabled={loading}
            className="mt-8 w-full bg-gold text-ink px-8 py-4 font-bold uppercase text-sm tracking-[0.15em] clip-frame-sm hover:bg-gold-light transition-colors disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </Reveal>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    api
      .get("/enquiries")
      .then((res) => setEnquiries(Array.isArray(res.data) ? res.data : []))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return enquiries;
    return enquiries.filter((e) =>
      [e.name, e.phone, branchName(e.branch), e.goal, e.email || ""].some((v) =>
        (v || "").toLowerCase().includes(q)
      )
    );
  }, [enquiries, search]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/enquiries/${id}/status`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status: data.status } : e)));
      toast.success(`Marked ${data.status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const newCount = enquiries.filter((e) => e.status === "new").length;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Signed in as {user.email}</p>
          <h1 className="font-display uppercase text-4xl">Enquiries</h1>
        </div>
        <div className="flex gap-4">
          <button
            data-testid="admin-refresh-button"
            onClick={load}
            className="border border-white/25 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-gold hover:text-gold transition-colors"
          >
            Refresh
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={onLogout}
            className="bg-gold text-ink px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] font-semibold hover:bg-gold-light transition-colors"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3 mb-8">
        <div data-testid="admin-stat-total" className="clip-frame border border-white/10 bg-panel p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Total</p>
          <p className="mt-2 font-display text-4xl">{enquiries.length}</p>
        </div>
        <div data-testid="admin-stat-new" className="clip-frame border border-gold/40 bg-panel p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">New</p>
          <p className="mt-2 font-display text-4xl text-gold-gradient">{newCount}</p>
        </div>
        <div data-testid="admin-stat-handled" className="clip-frame border border-white/10 bg-panel p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Handled</p>
          <p className="mt-2 font-display text-4xl">{enquiries.length - newCount}</p>
        </div>
      </div>

      <input
        data-testid="admin-search-input"
        className={`${inputCls} max-w-md mb-6`}
        placeholder="Search name, phone, branch…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading ? (
        <p className="text-white/40 text-sm" data-testid="admin-loading">Loading enquiries…</p>
      ) : filtered.length === 0 ? (
        <div data-testid="admin-empty-state" className="clip-frame border border-white/10 bg-panel p-12 text-center">
          <p className="font-display uppercase text-2xl text-white/60">No enquiries yet</p>
          <p className="mt-3 text-sm text-white/40">New leads from the contact form will appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto clip-frame border border-white/10">
          <table data-testid="admin-enquiries-table" className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="bg-panel2 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Phone</th>
                <th className="px-5 py-4">Branch</th>
                <th className="px-5 py-4">Goal</th>
                <th className="px-5 py-4">Received</th>
                <th className="px-5 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className="border-t border-white/5 hover:bg-panel transition-colors">
                  <td className="px-5 py-4 font-semibold" data-testid="admin-enquiry-row-name">
                    {e.name}
                    {e.message && <span className="block mt-1 text-xs text-white/40 font-normal max-w-[220px] truncate">{e.message}</span>}
                  </td>
                  <td className="px-5 py-4 font-mono text-xs text-white/70">{e.phone}</td>
                  <td className="px-5 py-4 text-white/70 text-xs">{branchName(e.branch)}</td>
                  <td className="px-5 py-4 text-white/70 text-xs">{e.goal}</td>
                  <td className="px-5 py-4 font-mono text-[10px] text-white/40">
                    {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </td>
                  <td className="px-5 py-4">
                    <select
                      data-testid="admin-enquiry-row-status"
                      value={e.status}
                      onChange={(ev) => setStatus(e.id, ev.target.value)}
                      className={`border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] focus:outline-none ${
                        e.status === "new"
                          ? "border-gold/50 text-gold bg-gold/10"
                          : e.status === "contacted"
                          ? "border-cyan-400/40 text-cyan-300 bg-cyan-400/10"
                          : "border-white/15 text-white/40 bg-white/5"
                      }`}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(null));
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
  };

  if (user === undefined) return <main className="min-h-[70vh]" />;

  return (
    <main className="pt-28 md:pt-36">
      {user ? <Dashboard user={user} onLogout={logout} /> : <Login onLogin={setUser} />}
    </main>
  );
}
